import type { FormEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import type { TimeFormatValue } from '@elfsight/configurator-contracts';
import type { TimeFormat } from '@elfsight/configurator-controls';
import type { TextControlProps } from '@elfsight/widget-ui';
import { isMobile, validateTime } from '@elfsight/widget-utils';
import { parse } from 'fast-time-input';
import { isIOS } from 'react-device-detect';
import type { FieldType } from '../../contexts';
import { type DropdownOption, TextDropdownField } from '../_components';
import type { CommonFieldProps } from '../types';
import { useFindNearestOption } from './hooks';
import {
  determineTimeFormat,
  getTimeOptions,
  parseAndFormatTime,
  removeLeadingZero
} from './utils';

export type TimeComponentProps = TextControlProps &
  CommonFieldProps & {
    timeFormat: TimeFormatValue;
    type?: FieldType;
  };

export function Time({
  value,
  type,
  timeFormat,
  fieldColor: fontColor,
  onChange,
  ...forwardingProps
}: TimeComponentProps) {
  const userLocale = window.navigator.language;
  const mobile = isMobile();

  const [currentTimeFormat, setCurrentTimeFormat] = useState<
    TimeFormat | undefined
  >(undefined);

  useEffect(() => {
    setCurrentTimeFormat(determineTimeFormat(timeFormat, userLocale));
  }, [userLocale, timeFormat]);

  const timeOptions = useMemo<DropdownOption[]>(() => {
    return getTimeOptions(currentTimeFormat);
  }, [currentTimeFormat]);

  const findNearestOption = useFindNearestOption(timeOptions);

  useEffect(() => {
    if (!value || !currentTimeFormat) {
      return;
    }

    const parsed24 = parseAndFormatTime({
      inputValue: value,
      validValue: value
    });

    if (!parsed24) {
      return;
    }

    const matchingOption = timeOptions.find((opt) => opt.value === parsed24);

    if (matchingOption && matchingOption.label !== value) {
      onChange?.(matchingOption.label);
    }
  }, [currentTimeFormat, timeOptions, value, onChange]);

  const parseInputValue = useCallback(
    (inputValue: string) => {
      return parseAndFormatTime({
        inputValue,
        validValue: value || ''
      });
    },
    [value]
  );

  const validateInput = useCallback((inputValue: string) => {
    return validateTime(inputValue);
  }, []);

  const formatValue = useCallback(
    (value: string) => removeLeadingZero(parse(value, currentTimeFormat)),
    [currentTimeFormat]
  );

  const handleNativeInput = useCallback(
    (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (!mobile) {
        return;
      }

      const { value: inputValue } = event.currentTarget;

      onChange?.(inputValue);
    },
    [mobile, onChange]
  );

  return (
    <StyledTextControl
      {...forwardingProps}
      value={value}
      options={timeOptions}
      fieldColor={fontColor}
      type={mobile ? 'time' : 'text'}
      actualInputType={type ?? 'time'}
      inputMode={mobile ? 'numeric' : 'text'}
      disableDropdown={mobile}
      isMobile={mobile}
      skipDisplayValueSync={isIOS}
      onChange={onChange}
      onInput={handleNativeInput}
      parseInputValue={parseInputValue}
      validateInput={validateInput}
      formatValue={formatValue}
      findNearestOption={findNearestOption}
    />
  );
}

const StyledTextControl = styled(TextDropdownField)`
  & input[type='time'],
  & input[type='time']:focus {
    -webkit-appearance: none;
    appearance: none;
    min-height: ${({ lineHeight, font }) =>
      `${(lineHeight ?? 1.32) * (font?.fontSize ?? 16)}px`} !important;
  }

  & input[type='time']::-webkit-date-and-time-value {
    text-align: left;
  }
`;
